<template>
  <Teleport to="body">
    <Transition name="phone-binding-dialog">
      <div
        v-if="dialogStage !== 'closed'"
        class="phone-binding-overlay"
        role="presentation"
        @click.self="handleCancel"
      >
        <section
          v-if="dialogStage === 'prompt'"
          class="phone-binding-prompt-card"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="promptTitleId"
          :aria-describedby="promptDescriptionId"
        >
          <header class="phone-binding-header">
            <h2 :id="promptTitleId">提示</h2>
            <button
              class="phone-binding-close"
              type="button"
              aria-label="关闭手机号绑定提示"
              @click="handleCancel"
            >
              <SvgIcon name="modal-close" :size="12" />
            </button>
          </header>

          <p :id="promptDescriptionId" class="phone-binding-prompt-description">
            检测到您的账号未绑定手机号。为保障账号安全和创作体验，请先绑定手机号后再继续创作。
          </p>

          <div class="phone-binding-prompt-actions">
            <button class="phone-binding-prompt-confirm" type="button" @click="openBindingDialog">
              绑定手机号
            </button>
          </div>
        </section>

        <section
          v-else
          class="phone-binding-card"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="bindingTitleId"
          :aria-describedby="bindingDescriptionId"
        >
          <header class="phone-binding-header">
            <h2 :id="bindingTitleId">{{ dialogTitle }}</h2>
            <button
              class="phone-binding-close"
              type="button"
              :aria-label="`关闭${dialogTitle}弹窗`"
              :disabled="submitting"
              @click="handleCancel"
            >
              <SvgIcon name="modal-close" :size="12" />
            </button>
          </header>

          <p :id="bindingDescriptionId" class="phone-binding-description">
            {{ dialogDescription }}
          </p>

          <form class="phone-binding-form" @submit.prevent="submitBinding">
            <div
              v-if="hasExistingPhone"
              class="phone-binding-input phone-binding-phone phone-binding-phone-bound"
            >
              <span>已绑定手机号</span>
              <strong>{{ maskedBoundPhone }}</strong>
            </div>

            <div v-else class="phone-binding-input phone-binding-phone">
              <el-popover
                v-model:visible="countryPickerVisible"
                placement="bottom-start"
                :width="280"
                :show-arrow="false"
                popper-class="phone-country-picker-popper"
                trigger="click"
              >
                <template #reference>
                  <button
                    class="phone-country-code"
                    type="button"
                    :aria-label="`选择国际区号，当前为 +${phoneAreaCode}`"
                    :aria-expanded="countryPickerVisible"
                  >
                    +{{ phoneAreaCode }}
                    <SvgIcon name="character-chevron-down" :size="12" />
                  </button>
                </template>

                <div class="phone-country-picker">
                  <input
                    v-model.trim="countrySearch"
                    class="phone-country-search"
                    type="search"
                    placeholder="搜索国家或区号"
                    aria-label="搜索国家或区号"
                  />
                  <div class="phone-country-list" role="listbox" aria-label="国际区号">
                    <button
                      v-for="country in filteredPhoneCountries"
                      :key="country.iso2"
                      class="phone-country-option"
                      :class="{ selected: country.iso2 === phoneCountryCode }"
                      type="button"
                      role="option"
                      :aria-selected="country.iso2 === phoneCountryCode"
                      @click="selectPhoneCountry(country)"
                    >
                      <span>{{ country.name }}</span>
                      <span>+{{ country.dialCode }}</span>
                    </button>
                    <p v-if="filteredPhoneCountries.length === 0" class="phone-country-empty">
                      未找到匹配的区号
                    </p>
                  </div>
                </div>
              </el-popover>

              <input
                ref="phoneInputRef"
                v-model.trim="form.phone"
                type="tel"
                inputmode="numeric"
                autocomplete="tel-national"
                maxlength="20"
                placeholder="输入手机号"
                aria-label="手机号"
                @input="normalizePhone"
              />
            </div>

            <div class="phone-code-row">
              <div class="phone-binding-input">
                <input
                  v-model.trim="form.code"
                  type="text"
                  inputmode="numeric"
                  autocomplete="one-time-code"
                  maxlength="6"
                  placeholder="输入验证码"
                  aria-label="验证码"
                  @input="normalizeCode"
                />
              </div>
              <button
                class="send-code-button"
                type="button"
                :disabled="sendingCode || countdown > 0"
                @click="sendCode"
              >
                {{
                  countdown > 0 ? `${countdown}s后重试` : sendingCode ? '发送中...' : '获取验证码'
                }}
              </button>
            </div>

            <div class="phone-binding-input password-input">
              <input
                v-model="form.password"
                :type="passwordVisible ? 'text' : 'password'"
                autocomplete="new-password"
                maxlength="20"
                placeholder="输入6-20位密码"
                aria-label="密码"
              />
              <button
                class="password-toggle"
                type="button"
                :aria-label="passwordVisible ? '隐藏密码' : '显示密码'"
                @click="passwordVisible = !passwordVisible"
              >
                <SvgIcon
                  :name="passwordVisible ? 'password-visible' : 'password-hidden'"
                  :size="20"
                />
              </button>
            </div>

            <div class="phone-binding-input password-input">
              <input
                v-model="form.confirmPassword"
                :type="confirmPasswordVisible ? 'text' : 'password'"
                autocomplete="new-password"
                maxlength="20"
                placeholder="重新输入6-20位密码"
                aria-label="确认密码"
              />
              <button
                class="password-toggle"
                type="button"
                :aria-label="confirmPasswordVisible ? '隐藏确认密码' : '显示确认密码'"
                @click="confirmPasswordVisible = !confirmPasswordVisible"
              >
                <SvgIcon
                  :name="confirmPasswordVisible ? 'password-visible' : 'password-hidden'"
                  :size="20"
                />
              </button>
            </div>

            <button class="phone-binding-confirm" type="submit" :disabled="submitting">
              {{ submitting ? submittingText : '确定' }}
            </button>
          </form>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
  import { computed, nextTick, onBeforeUnmount, reactive, ref, useId, watch } from 'vue';
  import { ElMessage } from 'element-plus';
  import {
    getCountries,
    getCountryCallingCode,
    parsePhoneNumberFromString,
    type CountryCode
  } from 'libphonenumber-js';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';
  import { userApis } from '@/axiosModel';
  import { useUserStore } from '@/store';
  import { isAuthDisabled } from '@/utils/login';
  import '@/utils/tn/css/style.css';
  import '@/utils/tn/js/tn_code.js';

  const BIND_PHONE_OPERATE_ID = 4;
  const RETRIEVE_PASSWORD_OPERATE_ID = 3;
  const PASSWORD_MIN_LENGTH = 6;
  const PASSWORD_MAX_LENGTH = 20;
  const DEFAULT_PHONE_COUNTRY = 'CN';
  const DEFAULT_AREA_CODE = '86';

  interface PhoneCountryOption {
    iso2: CountryCode;
    name: string;
    dialCode: string;
  }

  const countryDisplayNames = new Intl.DisplayNames(['zh-CN'], { type: 'region' });
  const phoneCountries: PhoneCountryOption[] = getCountries()
    .map((iso2) => ({
      iso2,
      name: countryDisplayNames.of(iso2) || iso2,
      dialCode: getCountryCallingCode(iso2)
    }))
    .sort((first, second) => {
      if (first.iso2 === DEFAULT_PHONE_COUNTRY) return -1;
      if (second.iso2 === DEFAULT_PHONE_COUNTRY) return 1;
      return first.name.localeCompare(second.name, 'zh-CN');
    });

  const userStore = useUserStore();
  const id = useId();
  const promptTitleId = `phone-binding-prompt-title-${id}`;
  const promptDescriptionId = `phone-binding-prompt-description-${id}`;
  const bindingTitleId = `phone-binding-title-${id}`;
  const bindingDescriptionId = `phone-binding-description-${id}`;
  const dialogStage = ref<'closed' | 'prompt' | 'binding'>('closed');
  const dialogMode = ref<'binding' | 'password'>('binding');
  const passwordSetupPhone = ref('');
  const sendingCode = ref(false);
  const submitting = ref(false);
  const countdown = ref(0);
  const passwordVisible = ref(false);
  const confirmPasswordVisible = ref(false);
  const phoneCountryCode = ref(DEFAULT_PHONE_COUNTRY);
  const phoneAreaCode = ref(DEFAULT_AREA_CODE);
  const countryPickerVisible = ref(false);
  const countrySearch = ref('');
  const phoneInputRef = ref<HTMLInputElement>();
  const form = reactive({
    phone: '',
    code: '',
    password: '',
    confirmPassword: ''
  });
  const hasExistingPhone = computed(
    () => dialogMode.value === 'password' && Boolean(passwordSetupPhone.value)
  );
  const maskedBoundPhone = computed(() => maskPhone(passwordSetupPhone.value));
  const dialogTitle = computed(() =>
    dialogMode.value === 'password' ? '设置密码' : '绑定手机号'
  );
  const dialogDescription = computed(() => {
    if (hasExistingPhone.value) return '验证已绑定手机号后设置登录密码';
    return '绑定手机并设置密码，账号更安全';
  });
  const submittingText = computed(() =>
    dialogMode.value === 'password' && hasExistingPhone.value ? '设置中...' : '绑定中...'
  );
  const filteredPhoneCountries = computed(() => {
    const search = countrySearch.value.toLocaleLowerCase();
    if (!search) return phoneCountries;

    return phoneCountries.filter(
      (country) =>
        country.name.toLocaleLowerCase().includes(search) ||
        country.iso2.toLocaleLowerCase().includes(search) ||
        `+${country.dialCode}`.includes(search) ||
        country.dialCode.includes(search)
    );
  });

  let countdownTimer: number | undefined;
  let activeCheck: Promise<boolean> | undefined;
  let resolveActiveCheck: ((bound: boolean) => void) | undefined;

  function ensurePhoneBound(): Promise<boolean> {
    if (isAuthDisabled()) return Promise.resolve(true);
    if (activeCheck) return activeCheck;

    activeCheck = checkPhoneBinding().finally(() => {
      activeCheck = undefined;
    });
    return activeCheck;
  }

  async function checkPhoneBinding() {
    if (isAuthDisabled()) return true;

    const loaded = await userStore.getUser();
    if (!loaded) {
      ElMessage.error('手机号绑定状态检测失败，请稍后重试');
      return false;
    }

    if (userStore.hasBoundPhone) return true;

    dialogStage.value = 'prompt';
    return new Promise<boolean>((resolve) => {
      resolveActiveCheck = resolve;
    });
  }

  async function openBindingDialog() {
    dialogMode.value = 'binding';
    dialogStage.value = 'binding';
    await nextTick();
    focusPhoneInput();
  }

  function openPasswordSetup(phone = userStore.userData.mobilePhone || userStore.userData.phone) {
    if (activeCheck) return activeCheck;

    activeCheck = showPasswordSetup(phone).finally(() => {
      activeCheck = undefined;
    });
    return activeCheck;
  }

  async function showPasswordSetup(phone: string) {
    dialogMode.value = 'password';
    passwordSetupPhone.value = phone.trim();
    phoneAreaCode.value = userStore.userData.areaCode.replace(/^\+/, '') || DEFAULT_AREA_CODE;
    dialogStage.value = 'binding';
    await nextTick();
    if (!passwordSetupPhone.value) focusPhoneInput();

    return new Promise<boolean>((resolve) => {
      resolveActiveCheck = resolve;
    });
  }

  function selectPhoneCountry(country: PhoneCountryOption) {
    phoneCountryCode.value = country.iso2;
    phoneAreaCode.value = country.dialCode;
    countryPickerVisible.value = false;
    countrySearch.value = '';
    focusPhoneInput();
  }

  function getValidatedPhone() {
    const phone = form.phone.trim();
    if (!phone || !phoneCountryCode.value) return undefined;

    const parsedPhone = parsePhoneNumberFromString(phone, phoneCountryCode.value as CountryCode);
    return parsedPhone?.isValid() ? parsedPhone : undefined;
  }

  function focusPhoneInput() {
    phoneInputRef.value?.focus();
  }

  function normalizePhone(event: Event) {
    form.phone = (event.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, 20);
  }

  function normalizeCode(event: Event) {
    form.code = (event.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, 6);
  }

  function sendCode() {
    const phone = hasExistingPhone.value ? undefined : getValidatedPhone();
    if (!hasExistingPhone.value && !phone) {
      ElMessage.warning('请输入正确的手机号');
      focusPhoneInput();
      return;
    }

    const account = hasExistingPhone.value ? passwordSetupPhone.value : phone!.nationalNumber;
    const areaCode = Number(phoneAreaCode.value);
    sendingCode.value = true;
    const captcha = window.tncode;
    captcha.init();
    captcha.setCheckData(() => ({
      account,
      ...(areaCode === 86 ? {} : { areaCode }),
      operateId: hasExistingPhone.value
        ? RETRIEVE_PASSWORD_OPERATE_ID
        : BIND_PHONE_OPERATE_ID
    }));
    captcha.onsuccess(() => {
      sendingCode.value = false;
      ElMessage.success('验证码已发送');
      startCountdown();
    });
    captcha.onerror((responseText) => {
      sendingCode.value = false;
      try {
        const response = JSON.parse(responseText) as { code?: string };
        if (response.code === 'ACCOUNT_EXIST') {
          ElMessage.warning({
            message: '该手机号已存在，请更换手机号',
            customClass: 'phone-binding-message-above-dialog',
            zIndex: 999999
          });
        } else if (response.code === 'SEND_RATE_LIMIT') {
          ElMessage.error({
            message: '绑定失败，当前IP发送验证码已达上限',
            customClass: 'phone-binding-message-above-dialog',
            zIndex: 999999
          });
        } else {
          showSendCodeRequestFailed();
        }
      } catch {
        showSendCodeRequestFailed();
      }
    });
    captcha.onhide(() => {
      document.getElementById('tncode_div')?.style.setProperty('display', 'none');
      sendingCode.value = false;
    });
    captcha.show.call(document.createElement('span'));
  }

  async function submitBinding() {
    const phone = hasExistingPhone.value ? undefined : getValidatedPhone();
    const code = form.code.trim();

    if (!hasExistingPhone.value && !phone) {
      ElMessage.warning('请输入正确的手机号');
      return;
    }
    if (!code) {
      ElMessage.warning('请输入验证码');
      return;
    }
    if (form.password.length < PASSWORD_MIN_LENGTH || form.password.length > PASSWORD_MAX_LENGTH) {
      ElMessage.warning('请输入6-20位密码');
      return;
    }
    if (form.password !== form.confirmPassword) {
      ElMessage.warning({
        message: '两次输入的密码不一致',
        customClass: 'phone-binding-message-above-dialog',
        zIndex: 999999
      });
      return;
    }

    submitting.value = true;
    try {
      const areaCode = Number(phoneAreaCode.value);
      const account = hasExistingPhone.value ? passwordSetupPhone.value : phone!.nationalNumber;

      if (hasExistingPhone.value) {
        await userApis.retrievePassword({
          account,
          password: form.password,
          verifyCode: code
        }).req;
      } else {
        await userApis.changeBindPhone({
          account,
          ...(areaCode === 86 ? {} : { areaCode }),
          verifyCode: code,
          password: form.password
        }).req;

        userStore.userData.mobilePhone = account;
        userStore.userData.phone = account;
        userStore.userData.areaCode = phoneAreaCode.value;
        userStore.userData.verifyPhone = 1;
      }

      userStore.userData.hasPassword = 1;
      ElMessage.success(hasExistingPhone.value ? '密码设置成功' : '手机号绑定成功');
      closeDialog(true);
    } catch (error) {
      ElMessage.error(
        getErrorMessage(
          error,
          hasExistingPhone.value ? '密码设置失败，请稍后重试' : '手机号绑定失败，请稍后重试'
        )
      );
    } finally {
      submitting.value = false;
    }
  }

  function startCountdown() {
    clearCountdown();
    countdown.value = 60;
    countdownTimer = window.setInterval(() => {
      countdown.value -= 1;
      if (countdown.value <= 0) clearCountdown();
    }, 1000);
  }

  function showSendCodeRequestFailed() {
    ElMessage.error({
      message: '请求失败',
      customClass: 'phone-binding-message-above-dialog',
      zIndex: 999999
    });
  }

  function clearCountdown() {
    if (countdownTimer !== undefined) {
      window.clearInterval(countdownTimer);
      countdownTimer = undefined;
    }
  }

  function handleCancel() {
    if (submitting.value) return;
    closeDialog(false);
  }

  function closeDialog(bound: boolean) {
    dialogStage.value = 'closed';
    clearCountdown();
    hideCaptcha();
    resolveActiveCheck?.(bound);
    resolveActiveCheck = undefined;
    resetForm();
  }

  function hideCaptcha() {
    if (document.getElementById('tncode_div')) window.tncode.hide();
  }

  function resetForm() {
    Object.assign(form, { phone: '', code: '', password: '', confirmPassword: '' });
    dialogMode.value = 'binding';
    passwordSetupPhone.value = '';
    passwordVisible.value = false;
    confirmPasswordVisible.value = false;
    phoneCountryCode.value = DEFAULT_PHONE_COUNTRY;
    phoneAreaCode.value = DEFAULT_AREA_CODE;
    countryPickerVisible.value = false;
    countrySearch.value = '';
    sendingCode.value = false;
    countdown.value = 0;
  }

  function maskPhone(phone: string) {
    return phone.replace(/^(\d{3})\d{4}(\d{4})$/, '$1****$2');
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && dialogStage.value !== 'closed') handleCancel();
  }

  function getErrorMessage(error: unknown, fallback: string) {
    if (typeof error !== 'object' || error === null) return fallback;
    const details = error as Record<string, unknown>;
    if (typeof details.errorMsg === 'string' && details.errorMsg) return details.errorMsg;
    if (typeof details.msg === 'string' && details.msg) return details.msg;
    if (typeof details.message === 'string' && details.message) return details.message;
    return fallback;
  }

  watch(
    dialogStage,
    (stage) => {
      if (stage !== 'closed') {
        document.addEventListener('keydown', handleKeydown);
      } else {
        document.removeEventListener('keydown', handleKeydown);
      }
    },
    { immediate: true }
  );

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', handleKeydown);
    clearCountdown();
    hideCaptcha();
    resolveActiveCheck?.(false);
  });

  defineExpose({ ensurePhoneBound, openPasswordSetup });
</script>

<style scoped>
  .phone-binding-overlay {
    position: fixed;
    inset: 0;
    z-index: 5000;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    padding: 24px;
    background: rgba(0, 0, 0, 0.5);
  }

  :global(.tncode_div) {
    z-index: 5001;
  }

  :global(.phone-country-picker-popper) {
    z-index: 5002 !important;
    padding: 0 !important;
    overflow: hidden;
    border-color: #e5e7eb !important;
    border-radius: 6px !important;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
  }

  :global(.phone-binding-message-above-dialog) {
    z-index: 999999 !important;
  }

  .phone-binding-card,
  .phone-binding-prompt-card {
    max-width: 100%;
    box-sizing: border-box;
    padding: 24px;
    overflow: hidden;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
    color: #333940;
    font-family: 'Microsoft YaHei', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
  }

  .phone-binding-card {
    width: 386px;
  }

  .phone-binding-prompt-card {
    width: 480px;
  }

  .phone-binding-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 24px;
  }

  .phone-binding-header h2 {
    margin: 0;
    font-family: Inter, 'Microsoft YaHei', sans-serif;
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
    letter-spacing: 0;
  }

  .phone-binding-close,
  .password-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    background: transparent;
    color: #999;
    cursor: pointer;
  }

  .phone-binding-close {
    width: 24px;
    height: 24px;
    border-radius: 6px;
  }

  .phone-binding-close:hover,
  .phone-binding-close:focus-visible {
    background: #f5f3fd;
    color: #6033e1;
    outline: none;
  }

  .phone-binding-close:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  .phone-binding-description {
    margin: 16px 0 0;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    letter-spacing: 0;
  }

  .phone-binding-prompt-description {
    min-height: 44px;
    margin: 16px 0 0;
    color: #000;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    letter-spacing: 0;
  }

  .phone-binding-prompt-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 10px;
  }

  .phone-binding-prompt-confirm {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 110px;
    height: 40px;
    padding: 0 20px;
    box-sizing: border-box;
    border: 1px solid #6033e1;
    border-radius: 6px;
    background: #6033e1;
    color: #fff;
    font: inherit;
    font-size: 14px;
    line-height: 22px;
    letter-spacing: 0;
    white-space: nowrap;
    cursor: pointer;
  }

  .phone-binding-prompt-confirm:hover,
  .phone-binding-prompt-confirm:focus-visible {
    border-color: #4f25c8;
    background: #4f25c8;
    outline: none;
  }

  .phone-binding-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 16px;
  }

  .phone-binding-input {
    display: flex;
    align-items: center;
    width: 100%;
    height: 44px;
    box-sizing: border-box;
    overflow: hidden;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #fff;
    transition: border-color 0.15s ease;
  }

  .phone-binding-input:focus-within {
    border-color: #6033e1;
  }

  .phone-binding-input input {
    min-width: 0;
    height: 100%;
    flex: 1;
    box-sizing: border-box;
    padding: 0 12px;
    border: 0;
    outline: 0;
    background: transparent;
    color: #333940;
    font: inherit;
    font-size: 14px;
    line-height: 22px;
    letter-spacing: 0;
  }

  .phone-binding-input input::placeholder {
    color: #999;
    opacity: 1;
  }

  .phone-binding-phone-bound {
    justify-content: space-between;
    padding: 0 12px;
    background: #f7f8fa;
    color: #606266;
  }

  .phone-binding-phone-bound span,
  .phone-binding-phone-bound strong {
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
  }

  .phone-binding-phone-bound strong {
    color: #333940;
  }

  .phone-country-code {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 64px;
    height: 44px;
    box-sizing: border-box;
    flex: 0 0 64px;
    padding: 0;
    border: 0;
    border-right: 1px solid #e5e7eb;
    background: transparent;
    color: #333940;
    font: inherit;
    font-size: 14px;
    line-height: 22px;
    cursor: pointer;
  }

  .phone-country-code:hover,
  .phone-country-code:focus-visible,
  .phone-country-code[aria-expanded='true'] {
    background: #f5f3fd;
    color: #6033e1;
    outline: none;
  }

  .phone-country-picker {
    background: #fff;
    color: #333940;
    font-family: 'Microsoft YaHei', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
  }

  .phone-country-search {
    width: calc(100% - 24px);
    height: 36px;
    margin: 12px;
    padding: 0 10px;
    box-sizing: border-box;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    outline: none;
    background: #fff;
    color: #333940;
    font: inherit;
    font-size: 14px;
    line-height: 22px;
  }

  .phone-country-search::placeholder {
    color: #999;
  }

  .phone-country-search:focus {
    border-color: #6033e1;
  }

  .phone-country-list {
    max-height: 240px;
    padding: 0 8px 8px;
    overflow-y: auto;
  }

  .phone-country-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    min-height: 38px;
    padding: 8px 10px;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: #333940;
    font: inherit;
    font-size: 14px;
    line-height: 22px;
    text-align: left;
    cursor: pointer;
  }

  .phone-country-option:hover,
  .phone-country-option:focus-visible {
    background: #f5f3fd;
    outline: none;
  }

  .phone-country-option.selected {
    background: #f5f3fd;
    color: #6033e1;
  }

  .phone-country-empty {
    margin: 0;
    padding: 24px 12px;
    color: #999;
    font-size: 14px;
    line-height: 22px;
    text-align: center;
  }

  .phone-code-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 102px;
    gap: 16px;
  }

  .send-code-button,
  .phone-binding-confirm {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 44px;
    box-sizing: border-box;
    border-radius: 6px;
    font: inherit;
    font-size: 14px;
    line-height: 22px;
    letter-spacing: 0;
    cursor: pointer;
  }

  .send-code-button {
    padding: 0 10px;
    border: 1px solid #e5e7eb;
    background: #fff;
    color: #333940;
    white-space: nowrap;
  }

  .send-code-button:hover:not(:disabled),
  .send-code-button:focus-visible {
    border-color: #6033e1;
    color: #6033e1;
    outline: none;
  }

  .send-code-button:disabled,
  .phone-binding-confirm:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }

  .password-input input {
    padding-right: 4px;
  }

  .password-toggle {
    width: 44px;
    height: 44px;
    flex: 0 0 44px;
    color: #ddd;
  }

  .password-toggle:hover,
  .password-toggle:focus-visible {
    color: #999;
    outline: none;
  }

  .phone-binding-confirm {
    width: 100%;
    height: 40px;
    margin-top: 0;
    border: 1px solid #6033e1;
    background: #6033e1;
    color: #fff;
  }

  .phone-binding-confirm:hover:not(:disabled),
  .phone-binding-confirm:focus-visible {
    border-color: #4f25c8;
    background: #4f25c8;
    outline: none;
  }

  .phone-binding-dialog-enter-active,
  .phone-binding-dialog-leave-active {
    transition: opacity 0.18s ease;
  }

  .phone-binding-dialog-enter-active .phone-binding-card,
  .phone-binding-dialog-leave-active .phone-binding-card,
  .phone-binding-dialog-enter-active .phone-binding-prompt-card,
  .phone-binding-dialog-leave-active .phone-binding-prompt-card {
    transition:
      opacity 0.18s ease,
      transform 0.18s ease;
  }

  .phone-binding-dialog-enter-from,
  .phone-binding-dialog-leave-to {
    opacity: 0;
  }

  .phone-binding-dialog-enter-from .phone-binding-card,
  .phone-binding-dialog-leave-to .phone-binding-card,
  .phone-binding-dialog-enter-from .phone-binding-prompt-card,
  .phone-binding-dialog-leave-to .phone-binding-prompt-card {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
  }

  @media (max-width: 480px) {
    .phone-binding-overlay {
      padding: 16px;
    }

    .phone-binding-card {
      width: 100%;
    }

    .phone-binding-prompt-card {
      width: 100%;
    }

    .phone-code-row {
      grid-template-columns: minmax(0, 1fr) 102px;
      gap: 8px;
    }
  }
</style>
